import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { categories, articles } from '@/mocks/data';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const filteredArticles = useMemo(() => {
    if (!currentCategory) return [];
    let result = articles.filter((a) => a.category === currentCategory.id);
    if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
    return result;
  }, [currentCategory, sortBy]);

  if (!currentCategory) {
    return <NotFound />;
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 奶妈动漫`,
    description: currentCategory.description,
    url: `https://naimadongman.com/category/${slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: currentCategory.name },
              ]}
            />

            <div className="mt-6 mb-10">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">
                {currentCategory.description} — 共 <strong>{filteredArticles.length}</strong> 篇内容
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-8">
              <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-3 mb-8">
              <span className="text-sm text-foreground-500">排序：</span>
              <button
                onClick={() => setSortBy('latest')}
                className={`text-sm px-3 py-1 rounded-full transition-colors whitespace-nowrap ${
                  sortBy === 'latest'
                    ? 'bg-primary-100 text-primary-600 font-medium'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                最新发布
              </button>
              <button
                onClick={() => setSortBy('popular')}
                className={`text-sm px-3 py-1 rounded-full transition-colors whitespace-nowrap ${
                  sortBy === 'popular'
                    ? 'bg-primary-100 text-primary-600 font-medium'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                热门推荐
              </button>
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    categorySlug={currentCategory.slug}
                    categoryName={currentCategory.name}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}